import { useAPIRoute } from '../../hooks';
import { useQuery } from 'react-query';
import { NeighborhoodServiceAreaInfo } from 'rwl-shared/dist/models/neighborhood';
import { useV2APIRoute } from '../../hooks/useAPIRoute';
import { fetchV2ServiceAreaByPostcode } from '../v2/fetchV2ServiceAreaByPostcode';

type useNeighborhoodProps = {
    accountId: string;
    postCode?: string;
};

export const useNeighborhood = ({
    accountId,
    postCode,
}: useNeighborhoodProps) => {
    const url = useAPIRoute('/serviceareas/neighborhood', 'v1');
    const v2NeighborhoodUrl = useV2APIRoute('neighborhood');
    const v2ServiceAreaContentUrl = useV2APIRoute('serviceArea/content');

    return useQuery<NeighborhoodServiceAreaInfo | undefined>(
        // v2 is part of the key so flipping the flag cannot serve a response the
        // other backend produced.
        ['neighborhood', accountId, postCode, v2NeighborhoodUrl ? 'v2' : 'v1'],
        async () => {
            if (!postCode || !accountId || postCode.trim() === '') {
                return;
            }

            if (v2NeighborhoodUrl && v2ServiceAreaContentUrl) {
                const result = await fetchV2ServiceAreaByPostcode({
                    neighborhoodUrl: v2NeighborhoodUrl,
                    serviceAreaContentUrl: v2ServiceAreaContentUrl,
                    accountId,
                    postCode,
                });
                // A result with no area means v2 answered and the account does not
                // serve this postcode — that is the answer, so return it. Only an
                // undefined result (v2 failed, already reported) falls through.
                if (result) {
                    return result.area;
                }
            }

            try {
                const result = await fetch(
                    `${url}?` +
                        new URLSearchParams({
                            accountId: accountId,
                            postCode: postCode,
                        }),
                    {
                        method: 'GET',
                    }
                );
                const text = await result.text();
                if (!text) {
                    return undefined;
                }
                return JSON.parse(text) as NeighborhoodServiceAreaInfo;
            } catch (e) {
                console.error('useNeighborhood error:', e);
                throw e;
            }
        },
        {
            staleTime: 1000 * 60 * 60 * 24, // 24 hours
        }
    );
};
